import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminDomain,
  type AdminDomainCatalog,
  type AdminDomainState,
  adminDomainHostnameSchema,
} from "@ternary/contracts"
import {
  Alert,
  Badge,
  type BadgeProps,
  Button,
  breakable,
  Card,
  Checkbox,
  ConfirmDialog,
  DescriptionList,
  Dialog,
  Field,
  Input,
} from "@ternary/ui"
import { type FormEvent, useId, useRef, useState } from "react"
import {
  addAdminDomain,
  deleteAdminDomain,
  setDefaultAdminDomain,
  verifyAdminDomain,
} from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { LiveAddress } from "../components/live-address.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { ViewOnlyNotice } from "../components/view-only.js"
import { adminDomainsQuery, SettingsHeader } from "./admin-settings-page.js"

/** While a domain waits on DNS or a certificate, the page looks again this often. */
const POLL_MS = 15_000
const waiting = new Set<AdminDomainState>(["pending", "conflict", "issuing"])

const STATES: Record<AdminDomainState, { label: string; note: string; tone: BadgeProps["tone"] }> =
  {
    conflict: {
      label: "In use elsewhere",
      note: "Another Ternary business uses this domain and its TXT record is still there. Once that record is gone from your DNS, the domain moves to you.",
      tone: "warning",
    },
    expired: {
      label: "Expired",
      note: "It did not go live within 7 days, or failed its check for 7 days. Put both records back, then choose Check now to start again.",
      tone: "neutral",
    },
    failing: {
      label: "Check failing",
      note: "Our daily check failed. We still serve the domain for now: put both records back within 7 days, or it will be removed.",
      tone: "danger",
    },
    issuing: {
      label: "Setting up HTTPS",
      note: "Both records are right. We are getting its certificate, which usually takes a few minutes.",
      tone: "primary",
    },
    live: {
      label: "Live",
      note: "Members can reach you here. Keep both records: we check them every day.",
      tone: "success",
    },
    pending: {
      label: "Waiting for DNS",
      note: "Add both records at your DNS host. We check every minute for the first hour, then less often. DNS changes can take a few hours to show.",
      tone: "neutral",
    },
  }

function checkedAt(value: string) {
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  )
}

/** Asks admin-api to change a domain, and puts the catalog it answers on the page. */
function useDomainAction<Value>(action: (input: Value) => Promise<AdminDomainCatalog | undefined>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: action,
    async onSuccess(catalog) {
      if (!catalog) {
        void queryClient.invalidateQueries({ queryKey: adminDomainsQuery.queryKey })
        return
      }
      // Grok A#17: a poll in flight would answer the catalog from before this change.
      await queryClient.cancelQueries({ queryKey: adminDomainsQuery.queryKey })
      queryClient.setQueryData(adminDomainsQuery.queryKey, catalog)
    },
  })
}

/** Add a domain, in a dialog: the page itself may scroll, a form may not. */
function AddDomainDialog({ onClose }: { onClose(): void }) {
  const formId = useId()
  const input = useRef<HTMLInputElement>(null)
  const [hostname, setHostname] = useState("")
  const [primary, setPrimary] = useState(true)
  const [invalid, setInvalid] = useState("")
  const add = useDomainAction(addAdminDomain)

  function submit(event: FormEvent) {
    event.preventDefault()
    const parsed = adminDomainHostnameSchema.safeParse(hostname)
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? "Enter a valid domain.")
      input.current?.focus()
      return
    }
    add.mutate(
      { hostname: parsed.data, primary },
      { onError: () => input.current?.focus(), onSuccess: onClose },
    )
  }

  const error = invalid || (add.error instanceof Error ? add.error.message : "")
  return (
    <Dialog
      description="Next we show the two DNS records to add at your DNS host."
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={add.isPending} type="submit">
            Add domain
          </Button>
        </>
      }
      onClose={onClose}
      open
      pending={add.isPending}
      title="Add a domain"
    >
      <form className="admin-stack" id={formId} noValidate onSubmit={submit}>
        <Field error={error} hint="Such as members.yourbrand.com" label="Domain">
          <Input
            autoCapitalize="none"
            autoComplete="off"
            maxLength={253}
            onChange={(event) => {
              setHostname(event.currentTarget.value)
              setInvalid("")
              if (add.isError) add.reset()
            }}
            ref={input}
            spellCheck={false}
            value={hostname}
          />
        </Field>
        <Checkbox
          checked={primary}
          label="Make it your address once it is live"
          onChange={(event) => setPrimary(event.currentTarget.checked)}
        />
      </form>
    </Dialog>
  )
}

/** The two records, each with Copy, as DNS hosts ask for them. */
function Records({ domain, onCopied }: { domain: AdminDomain; onCopied(notice: string): void }) {
  const id = useId()
  async function copy(type: string, value: string) {
    try {
      await navigator.clipboard.writeText(value)
      onCopied(`${type} value copied.`)
    } catch {
      onCopied(`Select the ${type} value and copy it.`)
    }
  }
  return (
    <div className="admin-stack">
      <h3 className="admin-heading" id={id}>
        Records to add
      </h3>
      <ul aria-labelledby={id} className="admin-domain-records">
        {domain.records.map((record) => (
          <li key={record.type}>
            <DescriptionList
              items={[
                { term: "Type", value: record.type },
                { term: "Name", value: breakable(record.name) },
                { term: "Value", value: breakable(record.value) },
              ]}
            />
            <Button
              onClick={() => void copy(record.type, record.value)}
              size="sm"
              variant="secondary"
            >
              Copy <span className="tny-visually-hidden">the {record.type} value</span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** What DNS showed at the last check, beside what the records ask for. */
function Observed({ domain }: { domain: AdminDomain }) {
  const id = useId()
  const { observed } = domain
  if (!observed) return <p className="admin-note">Not checked yet.</p>
  return (
    <div className="admin-stack">
      <h3 className="admin-heading" id={id}>
        What DNS shows now
      </h3>
      <ul aria-labelledby={id} className="admin-domain-observed">
        <li>
          {observed.cname.length > 0
            ? breakable(`CNAME: ${observed.cname.join(", ")}`)
            : "No CNAME record yet"}
        </li>
        <li>
          {observed.txt.length > 0
            ? breakable(`TXT: ${observed.txt.join(", ")}`)
            : "No TXT record yet"}
        </li>
        <li>
          Checked <time dateTime={observed.checkedAt}>{checkedAt(observed.checkedAt)}</time>
        </li>
      </ul>
    </div>
  )
}

function DomainCard({
  automatic,
  domain,
  writable,
}: {
  automatic: AdminDomainCatalog["automatic"]
  domain: AdminDomain
  writable: boolean
}) {
  const titleId = useId()
  const [notice, setNotice] = useState("")
  const [removing, setRemoving] = useState(false)
  const check = useDomainAction(verifyAdminDomain)
  const makePrimary = useDomainAction(setDefaultAdminDomain)
  const remove = useDomainAction(async (hostname: string) => {
    await deleteAdminDomain(hostname)
    return undefined
  })
  const state = STATES[domain.state]
  const failure = [check, makePrimary, remove].find((action) => action.error instanceof Error)
    ?.error as Error | undefined

  return (
    <section aria-labelledby={titleId} className="tny-card">
      <div className="tny-card-header">
        <h2 className="tny-card-title" id={titleId}>
          {domain.hostname}
        </h2>
        <div className="tny-card-actions">
          {domain.default ? <Badge tone="primary">Your address</Badge> : null}
          <Badge tone={state.tone}>{state.label}</Badge>
        </div>
      </div>
      <div className="tny-card-body admin-stack">
        <p className="admin-note">{state.note}</p>
        {domain.reason ? (
          <Alert role="none" tone="warning">
            {domain.reason}
          </Alert>
        ) : null}
        <Records domain={domain} onCopied={setNotice} />
        <Observed domain={domain} />
        {failure ? <Alert tone="danger">{failure.message}</Alert> : null}
        {writable ? (
          <div className="admin-domain-actions">
            <Button
              loading={check.isPending}
              onClick={() =>
                check.mutate(domain.hostname, {
                  onSuccess: () => setNotice("Checking now. This takes up to a minute."),
                })
              }
              size="sm"
              variant="secondary"
            >
              Check now
            </Button>
            {/* N4: only a live domain can be the address. */}
            {domain.state === "live" && !domain.default ? (
              <Button
                loading={makePrimary.isPending}
                onClick={() => makePrimary.mutate(domain.hostname)}
                size="sm"
                variant="secondary"
              >
                Make it your address
              </Button>
            ) : null}
            <Button onClick={() => setRemoving(true)} size="sm" variant="ghost">
              Remove
            </Button>
          </div>
        ) : null}
        <p className="admin-note" role="status">
          {notice}
        </p>
      </div>
      <ConfirmDialog
        body={
          domain.default && automatic
            ? `Your address goes back to ${automatic.hostname}. Members will no longer reach you at ${domain.hostname}.`
            : `Members will no longer reach you at ${domain.hostname}.`
        }
        confirmLabel="Remove domain"
        danger
        onCancel={() => setRemoving(false)}
        onConfirm={() => remove.mutate(domain.hostname, { onSettled: () => setRemoving(false) })}
        open={removing}
        pending={remove.isPending}
        title={`Remove ${domain.hostname}?`}
      />
    </section>
  )
}

/**
 * Settings → Domains (T17): the business's address, its custom domains from the two records to
 * add to live, and the owner's add, check now, make primary and remove. The worker does the
 * rest, so the page looks again every 15 seconds while a domain waits.
 */
export function AdminDomainsPage() {
  const query = useQuery({
    ...adminDomainsQuery,
    refetchInterval: (current) =>
      current.state.data?.domains.some((domain) => waiting.has(domain.state)) ? POLL_MS : false,
  })
  const backToAutomatic = useDomainAction(setDefaultAdminDomain)
  const [adding, setAdding] = useState(false)

  // Grok #17: only a first load that fails replaces the page; a failed refresh keeps the domains
  // (and an open dialog) and says so inline, as the next poll tries again.
  if (query.isPending || (query.isError && query.data == null)) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <SettingsHeader active="domains" />
        {query.isPending ? (
          <FormLoading fields={1} label="Loading your domains…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your domains"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }
  const catalog = query.data
  const available = catalog.provider !== "unavailable"
  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active="domains" />
      {query.isError ? (
        <Alert tone="warning">We couldn't refresh your domains. We'll try again.</Alert>
      ) : null}
      {!available ? (
        <Alert role="none" tone="info">
          Custom domains are not set up on this platform yet.
        </Alert>
      ) : catalog.writable ? null : (
        <ViewOnlyNotice />
      )}
      <Card title="Your address">
        <div className="admin-stack">
          <p className="admin-note">Signup links and emails use this address.</p>
          <LiveAddress url={catalog.defaultUrl} />
          {catalog.automatic && !catalog.automatic.default && catalog.writable ? (
            <div>
              <p className="admin-note">
                Your Ternary address is {breakable(catalog.automatic.hostname)}.
              </p>
              <Button
                loading={backToAutomatic.isPending}
                onClick={() => backToAutomatic.mutate(catalog.automatic?.hostname ?? "")}
                size="sm"
                variant="secondary"
              >
                Use your Ternary address
              </Button>
              {/* Grok A#38: as a domain card says why its action failed. */}
              {backToAutomatic.error instanceof Error ? (
                <Alert tone="danger">{backToAutomatic.error.message}</Alert>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
      <Card
        actions={
          available && catalog.writable ? (
            <Button onClick={() => setAdding(true)} size="sm">
              Add a domain
            </Button>
          ) : null
        }
        title="Custom domains"
      >
        <p className="admin-note">
          {catalog.domains.length > 0
            ? "Members reach you at each domain once it is live."
            : "Use your own domain, such as members.yourbrand.com. You add two DNS records, and we do the rest."}
        </p>
      </Card>
      {adding ? <AddDomainDialog onClose={() => setAdding(false)} /> : null}
      {catalog.domains.map((domain) => (
        <DomainCard
          automatic={catalog.automatic}
          domain={domain}
          key={domain.hostname}
          writable={catalog.writable}
        />
      ))}
      {catalog.writable && catalog.domains.some((domain) => domain.state !== "live") ? (
        <Card title="Tips for your DNS host">
          <ul className="admin-domain-tips">
            <li>
              Most DNS hosts want only the part before your domain in the name field, such as
              members and _ternary.members.
            </li>
            <li>On Cloudflare, set the CNAME record to DNS only (the grey cloud).</li>
            <li>
              For a domain with nothing before it, such as yourbrand.com, use an ALIAS or ANAME
              record, or CNAME flattening, to the same value. An A record does not work.
            </li>
          </ul>
        </Card>
      ) : null}
    </section>
  )
}
